import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Upload, FileText, Trash2 } from 'lucide-react';

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const DocumentsTab = ({
  onOpenDocument,            // 10/26: files behind /api/documents/files need the login token — the parent fetches them
  documents,
  documentDialogOpen,
  setDocumentDialogOpen,
  documentForm,
  setDocumentForm,
  onAddDocument,
  onDeleteDocument
}) => {
  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle>Documents</CardTitle>
          <Dialog open={documentDialogOpen} onOpenChange={setDocumentDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm">
                <Upload className="h-4 w-4 mr-2" />
                Add Document
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Document</DialogTitle>
              </DialogHeader>
              <form onSubmit={onAddDocument} className="space-y-4">
                <div>
                  <Label htmlFor="doc_name">Document Name</Label>
                  <Input
                    id="doc_name"
                    value={documentForm.document_name}
                    onChange={(e) => setDocumentForm({...documentForm, document_name: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="doc_type">Document Type</Label>
                  <Select
                    value={documentForm.document_type}
                    onValueChange={(value) => setDocumentForm({...documentForm, document_type: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Contract">Contract</SelectItem>
                      <SelectItem value="ISO">ISO Application</SelectItem>
                      <SelectItem value="Bank Statement">Bank Statement</SelectItem>
                      <SelectItem value="Invoice">Invoice</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="file_url">File URL</Label>
                  <Input
                    id="file_url"
                    value={documentForm.file_url}
                    onChange={(e) => setDocumentForm({...documentForm, file_url: e.target.value})}
                    placeholder="https://example.com/document.pdf"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="notes">Notes</Label>
                  <Input
                    id="notes"
                    value={documentForm.notes}
                    onChange={(e) => setDocumentForm({...documentForm, notes: e.target.value})}
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <Button type="button" variant="outline" onClick={() => setDocumentDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit">Add Document</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {documents.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            No documents uploaded for this deal.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-blue-100">
                  <TableHead>Document Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Uploaded By</TableHead>
                  <TableHead>Uploaded At</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {documents.map((doc) => (
                  <TableRow key={doc.id}>
                    <TableCell>
                      <button type="button" data-testid={`open-document-${doc.id}`}
                        onClick={() => (onOpenDocument ? onOpenDocument(doc) : window.open(doc.file_url, '_blank', 'noopener'))}
                        className="text-blue-600 hover:underline flex items-center text-left">
                        <FileText className="h-4 w-4 mr-2" />
                        {doc.document_name}
                      </button>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{doc.document_type}</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {doc.uploaded_by_name || 'Unknown'}
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {formatDate(doc.uploaded_at)}
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {doc.notes || '-'}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => onDeleteDocument(doc.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DocumentsTab;
